import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './dark-theme.css';
import { logError } from './services/errorLog';
import { ErrorBoundary } from './components/ErrorBoundary';

// Suppress Firebase COOP warnings - these are expected behavior and don't affect functionality
// Firebase's popup authentication needs to check window.closed/window.close, which triggers
// COOP warnings when the policy is set. These warnings are informational only.
if (typeof window !== 'undefined' && typeof console !== 'undefined') {
  const originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    // Check if this is a Firebase COOP warning
    const message = args[0];
    if (typeof message === 'string') {
      // Suppress specific Firebase COOP warnings
      if (
        message.includes('Cross-Origin-Opener-Policy policy would block the window.closed call') ||
        message.includes('Cross-Origin-Opener-Policy policy would block the window.close call')
      ) {
        // Silently suppress these warnings
        return;
      }
    }
    // For all other errors, call the original console.error
    originalConsoleError.apply(console, args);
  };
}

// Global automatic error capture — reports unhandled errors to Firestore errorLogs
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    // Firebase Auth's browserPopupRedirectResolver throws this on page load when a previous
    // popup/redirect session was interrupted. It's a known SDK internal bug — not actionable.
    if ((event.message || '').includes('INTERNAL ASSERTION FAILED')) return;
    logError(event.error || new Error(event.message), `${event.filename}:${event.lineno}`, 'unhandled_error');
  });
  window.addEventListener('unhandledrejection', (event) => {
    const msg = event.reason instanceof Error ? event.reason.message : String(event.reason ?? '');
    if (msg.includes('INTERNAL ASSERTION FAILED')) return;
    logError(event.reason, 'unhandledrejection', 'unhandled_rejection');
  });
}

// Ask the browser to keep our IndexedDB (the Firestore offline write queue) from
// being evicted under storage pressure. Best-effort: the grant can be denied and
// isn't user-correctable in-app; we log the result to learn the real grant rate
// across the crew's devices. See review/04-offline-write-spec.md §5.3.
if (typeof navigator !== 'undefined' && navigator.storage?.persist) {
  navigator.storage.persisted()
    .then((already) => (already ? true : navigator.storage.persist()))
    .then((granted) => {
      // Only log the DENIED case — that's the outcome worth knowing (the write
      // queue is at eviction risk). Logging granted=true just floods the Error
      // Log with success noise that reads as a fault.
      if (!granted) {
        logError(new Error('storage.persist denied'), 'storage_persist', 'manual');
      }
    })
    .catch(() => {});
}

// Unregister the old FCM stub SW (firebase-messaging-sw.js) from browsers that have it cached.
// The new combined SW (/sw.js) is registered by useServiceWorkerUpdate via vite-plugin-pwa.
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.getRegistrations()
      .then((registrations) => {
        registrations.forEach((reg) => {
          const url = reg.active?.scriptURL ?? reg.installing?.scriptURL ?? reg.waiting?.scriptURL ?? '';
          if (url.includes('firebase-messaging-sw')) {
            reg.unregister();
          }
        });
      })
      .catch(() => {});
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

// Error component for configuration errors
const ConfigErrorDisplay = ({ error }: { error: Error }) => (
  <div className="min-h-screen bg-slate-100 dark:bg-slate-950 flex items-center justify-center p-4">
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-8 max-w-2xl w-full border border-slate-200 dark:border-slate-700">
      <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      
      <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-2 text-center">
        Configuration Error
      </h1>
      
      <p className="text-slate-600 dark:text-slate-400 mb-4 text-center">
        The application is missing required configuration. Please contact the administrator.
      </p>

      <div className="bg-slate-100 dark:bg-slate-900 rounded-lg p-4 mb-6 text-left">
        <code className="text-sm text-red-600 dark:text-red-400 font-mono whitespace-pre-wrap break-words">
          {error.message}
        </code>
      </div>

      <div className="text-center text-sm text-slate-600 dark:text-slate-400">
        <p>This error typically occurs when environment variables are not set during the build process.</p>
        <p className="mt-2">Please check the deployment configuration and ensure all required variables are set.</p>
      </div>
    </div>
  </div>
);

const dismissSplash = () => {
  // Two rAF calls ensure React has completed its first paint before we fade out
  requestAnimationFrame(() => requestAnimationFrame(() => {
    const splash = document.getElementById('splash');
    if (!splash) return;
    splash.style.opacity = '0';
    setTimeout(() => splash.remove(), 320);
  }));
};

// Initialize app with error handling
const initApp = async () => {
  const root = ReactDOM.createRoot(rootElement);

  try {
    // Import firebase to check for initialization errors
    const firebaseModule = await import('./firebase');

    if (firebaseModule.initializationError) {
      root.render(<ConfigErrorDisplay error={firebaseModule.initializationError} />);
      dismissSplash();
      return;
    }

    // Import App component
    const AppModule = await import('./App');
    const App = AppModule.default;

    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <App onAuthReady={dismissSplash} />
        </ErrorBoundary>
      </React.StrictMode>
    );
  } catch (error: any) {
    // Catch any import or initialization errors
    const configError = error instanceof Error ? error : new Error(String(error));
    root.render(<ConfigErrorDisplay error={configError} />);
    dismissSplash();
  }
};

// Start the app
initApp();